import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePlatformName } from '../../modules/appConfig'
import useClickoutHandler from '../../utils/useClickoutHandler'
import { ReactComponent as RevenueIcon } from '../../assets/images/icon-revenue.svg'
import Button from '../Button'

type ExitModalProps = {
  open: boolean
  onClose?: () => void
}

const ExitModal: React.FC<ExitModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation()
  const platformName = usePlatformName()
  const [x, setX] = useState(0)
  const [modalX, setModalX] = useState(0)

  const width = 500

  /**
   * track mouse position
   */
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (open) {
        return
      }
      setX(event.clientX)
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [open])

  /**
   * set modal position
   */
  useEffect(() => {
    let mouseX = x === undefined ? (window.innerWidth - width) / 2 : (x - width / 2)
    mouseX = Math.min(mouseX, window.innerWidth - width - 50)
    mouseX = Math.max(mouseX, 0)

    setModalX(mouseX)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const triggerRef = useRef<HTMLDivElement>(null)
  useClickoutHandler(triggerRef, () => { onClose?.() })

  if (!open) {
    return null
  }

  return (
    <div
      className="fixed inset-0 z-[99999999] flex size-full items-center justify-center bg-white/50 px-[15px] backdrop-blur-sm sm:px-[25px] md:top-[25px] md:size-auto md:items-start md:justify-normal md:bg-transparent md:backdrop-blur-none"
      style={{
        left: modalX,
      }}
    >
      <div
        ref={triggerRef}
        className="w-full max-w-[500px] overflow-hidden rounded-2xl shadow-[0px_0px_18px_3px_rgba(0,_0,_0,_0.15)] md:w-[500px]"
      >
        <div className="relative flex h-[425px] items-center justify-center bg-white bg-gradient-to-br from-white via-primary/15 to-white sm:h-[350px]">

          <div
            className="z-1 absolute top-[-1200px] size-[1400px] rounded-full bg-white/70"
          />

          <div className="z-2 absolute inset-0 px-4 py-8 text-center md:px-12">
            <div className="mb-2 flex justify-center">
              <RevenueIcon className="h-[35px] [&_circle]:fill-primary" />
            </div>
            <h1 className="font-body2 text-xl leading-tight text-black">
              <div className="mb-1 text-2xl font-bold text-primary md:text-3xl">
                { t('pages.quotesRequest.exitModal.title') }
              </div>

              { platformName
                ? t('pages.quotesRequest.exitModal.subtitlePlatform', { platformName })
                : t('pages.quotesRequest.exitModal.subtitle') }
            </h1>

            <div className="mb-4 mt-16 px-6 font-body2 text-base text-black">
              { t('pages.quotesRequest.exitModal.footer') }
            </div>

            <Button onClick={onClose}>
              { t('pages.quotesRequest.exitModal.cta') }
            </Button>

          </div>
        </div>
      </div>
    </div>
  )
}

export default ExitModal
